import { useContext, useEffect, useState } from "react";
import "./PackingList.scss";
import { Link } from "react-router-dom";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import IGetPackingListsDTO from "../../../models/DTO/IGetPackingListsDTO";
import IOnGetPackingListsDTO from "../../../models/DTO/IOnGetPackingListsDTO";
import IOnUpdatePackingListDTO from "../../../models/DTO/IOnUpdatePackingListDTO";
import IUpdatePackingListDTO from "../../../models/DTO/IUpdatePackingListDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IBreadcrumbs from "../../../models/IBreadcrumbs";
import IOwTableCols from "../../../models/IOwTableCols";
import IPacking_List from "../../../models/IPacking_List";
import ISupplierModel from "../../../models/ISupplierModel";
import IUserUI from "../../../models/IUserUI";
import IVendorModel from "../../../models/IVendorModel";
import UserTypesEnum from "../../../models/UserTypesEnum";
import UserService from "../../../services/UserService";
import Breadcrumbs from "../../shared/Breadcrumbs/Breadcrumbs";
import OwTable from "../../shared/OwTable/OwTable";
import OwTitle from "../../shared/OwTitle/OwTitle";
import PaginationBar from "../../shared/PaginationBar/PaginationBar";
import { PackingListSearch } from "./PackingListSearch/PackingListSearch";
import { Socket } from "socket.io-client";
import UiContext from "../../../contexts/UiContext";
import { SocketContext } from "../../../contexts/SocketContext";
import UserContext from "../../../contexts/UserContext";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import SupplierContext from "../../../contexts/SupplierContext";
import INotification from "../../../models/INotification";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import { ConfirmDeleteOverlayFn } from "../../shared/ConfirmDeleteOverlayFn/ConfirmDeleteOverlayFn";
import { OwDeleteEditActions } from "../../shared/OwDeleteEditActions/OwDeleteEditActions";
import { SearchSection } from "../../shared/SearchSection/SearchSection";

type Props = {
    socket: Socket;
    toggleSidebar: (shouldBeVisible?: boolean) => void;
    setSidebarComponent: (component: any) => void;
    setActivityIndicator: (activityOn: boolean) => void;
    notifyFn: (data: IErrorDTO | null, _notification?: any, isFormatted?: boolean) => void;
    userModel: IUserUI;
    vendorModel: IVendorModel;
    supplierModel: ISupplierModel;
};

const PackingList = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const actionsRenderFn = (row: IPacking_List, index: number) => {
        return (
            <div>
                {userModel.supplierUserToken?.user_type_id === UserTypesEnum.ADMIN ? (
                    <OwDeleteEditActions
                        isEditMode={false}
                        onTrashClickFn={(e: any) => {
                            confirmDelete(row, index);
                        }}
                        onEditClickFn={() => {}}
                        hideEdit={true}
                    />
                ) : null}
            </div>
        );
    };

    const nameRenderFn = (row: IPacking_List) => {
        return (
            <div>
                <i
                    className={"me-2 fad " + (row.is_locked ? "fa-lock-alt" : "fa-lock-open-alt")}
                    title={row.is_locked ? "Locked" : "Unlocked"}
                />
                <Link className={"pointer"} to={`/packing_lists/${row.guid}`}>
                    {row.name}
                </Link>
                <i className={"ms-2 fas fa-box " + (row.shipment_id ? "" : "text-muted")}></i>
            </div>
        );
    };

    const pickListRenderFn = (row: IPacking_List) => {
        if (row.Pick_List) {
            return (
                <div>
                    <Link to={`/pick_lists/${row.Pick_List.guid}`}>{row.Pick_List.name}</Link>
                </div>
            );
        } else {
            return "";
        }
    };

    const dateRenderFn = (dateStr: string) => {
        return UserService.parseDate(dateStr);
    };

    const ordersRenderFn = (row: IPacking_List) => {
        if (row.Orders) {
            return row.Orders.map((order) => {
                return (
                    <div key={order.guid}>
                        <Link to={`/orders/${order.guid}/entry`}>{order.name}</Link>
                    </div>
                );
            });
        } else {
            return "";
        }
    };

    const getTableCols = (): IOwTableCols[] => {
        return [
            {
                isActionsCol: true,
                title: "",
                valueProp: "",
                renderFn: actionsRenderFn,
                renderFnEmitsRow: true,
            },
            {
                title: "name",
                valueProp: "name",
                renderFn: nameRenderFn,
                renderFnEmitsRow: true,
            },
            {
                title: "pick_list",
                valueProp: "",
                renderFn: pickListRenderFn,
                renderFnEmitsRow: true,
            },
            {
                title: "vendor",
                isVendorOverride: true,
                valueProp: "Vendor",
                valueSubProp: "name",
            },
            {
                title: "orders",
                valueProp: "Orders",
                renderFn: ordersRenderFn,
                renderFnEmitsRow: true,
            },
            {
                title: "created_at",
                valueProp: "created_at",
                renderFn: dateRenderFn,
            },
            {
                title: "updated_at",
                valueProp: "updated_at",
                renderFn: dateRenderFn,
            },
        ];
    };

    const [tableCols, setTableCols] = useState<IOwTableCols[]>(getTableCols());
    const [crumbs, setCrumbs] = useState<IBreadcrumbs[]>([
        { title: "Dashboard", url: "/dashboard" },
        { title: "Packing Lists", url: "/packing_lists" },
    ]);
    const [isDataFetching, setIsDataFetching] = useState(false);
    const [isInitialData, setIsInitialData] = useState(true);
    const [packingLists, setPackingLists] = useState<IPacking_List[]>([]);
    const [deleteElem, setDeleteElem] = useState<string | null>(null);
    const [deleteRow, setDeleteRow] = useState<IPacking_List | null>(null);
    const [offset, setOffset] = useState(0);
    const [limit, setLimit] = useState(10);
    const [totalPackingLists, setTotalPackingLists] = useState(0);
    const [queryString, setQueryString] = useState<string | undefined>();

    const onGetPackingLists = (data: IOnGetPackingListsDTO) => {
        setIsInitialData(false);
        setIsDataFetching(false);
        setPackingLists(data.packingLists);
        setTotalPackingLists(data.totalPackingLists);
    };

    const onUpdatePackingList = (data: IOnUpdatePackingListDTO) => {
        const notification: INotification = {
            message: "successfully_updated_packing_list",
            position: NotificationPositionEnum.bc,
            level: NotificationLevelEnum.success,
        };

        uiContext.notifyFn(null, notification);

        setDeleteRow(null);
        setDeleteElem(null);

        getPackingLists();
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.OrderOnGetPackingLists, fn: onGetPackingLists },
        { endpoint: EndpointsEnum.OrderOnUpdatePackingList, fn: onUpdatePackingList },
    ];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        document.title = "Orderwerks >> Packing Lists";

        return () => {
            for (const _socket of sockets) {
                socket.removeListener(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    useEffect(() => {
        getPackingLists();
    }, [queryString, offset, limit]);

    const confirmDelete = (row: IPacking_List, index: number) => {
        setDeleteElem(`row-${index}`);
        setDeleteRow(row);
    };

    const getPackingLists = () => {
        const payload: IGetPackingListsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            limit,
            offset,
        };

        if (queryString) {
            payload.queryString = queryString;
        }

        socket.emit(EndpointsEnum.OrderGetPackingLists, payload);

        setIsDataFetching(true);
    };

    const cancelDelete = () => {
        setDeleteRow(null);
        setDeleteElem(null);
    };

    const commitDelete = () => {
        if (deleteRow) {
            const payload: IUpdatePackingListDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                deleteFlag: true,
                packingListGuid: deleteRow.guid,
                rows: [],
                sourceRows: [],
            };

            socket.emit(EndpointsEnum.OrderUpdatePackingList, payload);

            setDeleteRow(null);
            setDeleteElem(null);
        }
    };

    const onPaginationChanged = (_offset: number, _limit: number) => {
        setOffset(_offset);
        setLimit(_limit);
    };

    const search = (queryString?: string) => {
        setQueryString(queryString);
    };

    return (
        <div className="PackingList position-relative">
            {/*<Breadcrumbs crumbs={crumbs} />*/}

            <div className="row mt-2">
                <div className="col-12 d-flex align-items-center justify-items-center">
                    <OwTitle className={"me-3"} title="packing_lists" leftIconName="fad fa-box-check" />
                    {/*<OwButton className={"btn-warning"} title={"create"} leftIconClass={"fas fa-plus"} />*/}
                </div>
            </div>

            <div className={"mt-2 mb-2"}>
                <SearchSection>
                    <PackingListSearch
                        userModel={userModel}
                        supplierModel={supplierModel}
                        socket={socket}
                        setActivityIndicator={props.setActivityIndicator}
                        notifyFn={props.notifyFn}
                        searchFn={search}
                    />
                </SearchSection>
            </div>

            <div className={"position-relative"}>
                <ConfirmDeleteOverlayFn
                    targetElem={deleteElem}
                    handleConfirm={commitDelete}
                    handleCancel={cancelDelete}
                    leftAdjustment={0}
                    skipMainView={true}
                    nestedParentElem={"packing-lists"}
                />

                <OwTable
                    id={"packing-lists"}
                    columns={tableCols}
                    dataSet={packingLists}
                    userModel={userModel}
                    supplierModel={supplierModel}
                    dataFetch={isDataFetching}
                    initialData={isInitialData}
                />
            </div>

            <div>
                <PaginationBar offset={offset} limit={limit} totalRecords={totalPackingLists} onPaginationChanged={onPaginationChanged} />
            </div>
        </div>
    );
};

export default PackingList;
